import { useState, useEffect } from "react";
import { Row, Col, Table, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/formatNumber";
import Loading from "../../../components/core/blocks/Loading";
import { IoRefreshCircleSharp } from "react-icons/io5";
import { useFetch } from "../../../hooks/api";
import { useQueryClient } from "@tanstack/react-query";
import ExternalProductLink from "../../../components/custom/merchandise/ExternalProductLink";

interface PriceDetailsProps {
  isHistoryDataLoading: boolean;
  historyData: {
    [key: string]: {
      list: number;
      offer_noti: number;
      b2c_offer: number;
      sale: number;
      standard: number;
      min_allowed: number;
      max_allowed: number;
      external: number;
      reference: number;
      trellis_reference: number;
      customer_reference: number;
      product_title: string;
      product_id: number;
      product_image: string;
      asin: string;
      sku: string;
    };
  };
  marketPlace: { marketPlace: string };
  productId: string;
  showProductDetails?: boolean;
}

const PriceDetails: React.FC<PriceDetailsProps> = ({
  isHistoryDataLoading,
  historyData,
  marketPlace,
  productId,
  showProductDetails = false,
}) => {
  const { t } = useTranslation(["merchandising", "common"]);
  const queryClient = useQueryClient();
  const [refreshEnabled, setRefreshEnabled] = useState(false);

  const { data: liveListing } = useFetch(
    ["historyData", productId],
    `/pricing/listing_refresh`,
    {
      product_id: productId,
    },
    { enabled: refreshEnabled },
  );

  useEffect(() => {
    if (liveListing) {
      void queryClient.invalidateQueries({
        queryKey: ["price_history"],
      });
      setRefreshEnabled(false);
    }
  }, [liveListing, queryClient]);

  return (
    <Row className="p-3 text-left">
      <Col>
        {isHistoryDataLoading && <Loading fullPage={false} height={"300px"} />}

        {!isHistoryDataLoading && historyData[productId] === undefined && (
          <Row>
            <Col xs={12}>
              <div className="text-center mx-5">
                <span className="fs-4">{t(($) => $.merchandising["No Pricing Data Available"])}</span>
              </div>
            </Col>
          </Row>
        )}

        {!isHistoryDataLoading && historyData[productId] !== undefined && (
          <Row>
            <Col style={{ borderBottom: "1px solid #e0e0e0" }} className="py-2" xxl={12} xs={12}>
              {!showProductDetails && (
                <Row>
                  <Col className="text-end mb-3" xxl={6} xs={12}>
                    <Button
                      variant="primary"
                      className="m-0 py-2 px-4 fs-5"
                      style={{ cursor: "pointer" }}
                      onClick={() => setRefreshEnabled(true)}
                    >
                      <span>
                        {refreshEnabled ?
                          <i className="fa fa-spinner fa-spin fa-2x fs-5"></i>
                        : <IoRefreshCircleSharp style={{ cursor: "pointer" }} />}
                      </span>
                      <span className="ms-2" style={{ cursor: "pointer" }}>
                        {refreshEnabled ?
                          t(($) => $.common["Loading..."])
                        : t(($) => $.merchandising["Refresh Prices"])}
                      </span>
                    </Button>
                  </Col>
                </Row>
              )}

              {showProductDetails && (
                <Row>
                  <Col>
                    {/* Flex box with image of the left a product title on the right */}
                    <div className="d-flex" style={{ textAlign: "left" }}>
                      <img
                        src={historyData[productId].product_image}
                        alt={historyData[productId].product_title}
                        className="img-fluid me-3"
                        style={{ maxWidth: "100px" }}
                      />
                      <div>
                        <h3 style={{ marginBottom: "0.0rem", marginTop: "0px", fontSize: "16px" }}>
                          {historyData[productId].product_title}
                        </h3>
                        <div className="pt-2 fs-5">
                          {historyData[productId].asin} ({historyData[productId].sku})
                          <span className="ps-3">
                            <ExternalProductLink asin={historyData[productId].asin} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              <Row>
                <Col xxl={6} xs={12}>
                  <Table borderless hover>
                    <tbody>
                      <tr>
                        <th>{t(($) => $.merchandising["List Price"])}</th>
                        <td className="text-end">
                          {historyData[productId].list > 0 ?
                            formatCurrency(historyData[productId].list, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : "-"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t(($) => $.merchandising["Item Offer Price"])}</th>
                        <td className="text-end">
                          {historyData[productId].offer_noti > 0 ?
                            formatCurrency(historyData[productId].offer_noti, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : historyData[productId].b2c_offer ?
                            formatCurrency(historyData[productId].b2c_offer, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : "-"}
                        </td>
                      </tr>

                      <tr>
                        <th>{t(($) => $.merchandising["Your Price"])}</th>
                        <td className="text-end">
                          {historyData[productId].standard > 0 ?
                            formatCurrency(historyData[productId].standard, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : "-"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t(($) => $.merchandising["Discounted Price"])}</th>
                        <td className="text-end">
                          {historyData[productId].sale > 0 ?
                            formatCurrency(historyData[productId].sale, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : "-"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t(($) => $.merchandising["Amazon's Minimum Price"])}</th>
                        <td className="text-end">
                          {historyData[productId].min_allowed > 0 ?
                            formatCurrency(historyData[productId].min_allowed, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : "-"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t(($) => $.merchandising["Amazon's Maximum Price"])}</th>
                        <td className="text-end">
                          {historyData[productId].max_allowed > 0 ?
                            formatCurrency(historyData[productId].max_allowed, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : "-"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t(($) => $.merchandising["Competitive Price"])}</th>
                        <td className="text-end">
                          {historyData[productId].external > 0 ?
                            formatCurrency(historyData[productId].external, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : "-"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t(($) => $.merchandising["Reference Price"])}</th>
                        <td className="text-end">
                          {historyData[productId].reference > 0 ?
                            formatCurrency(historyData[productId].reference, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : "-"}
                        </td>
                      </tr>
                      <tr style={{ borderTop: "1px solid #e0e0e0" }}>
                        <th>{t(($) => $.merchandising["Trellis Reference Price"])}</th>
                        <td className="text-end">
                          {historyData[productId].trellis_reference > 0 ?
                            formatCurrency(historyData[productId].trellis_reference, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : "-"}
                        </td>
                      </tr>
                      <tr>
                        <th>{t(($) => $.merchandising["Customer Reference Price"])}</th>
                        <td className="text-end">
                          {historyData[productId].trellis_reference > 0 ?
                            formatCurrency(historyData[productId].customer_reference, {
                              marketPlace: marketPlace.marketPlace,
                            })
                          : "-"}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
};

export default PriceDetails;
